import {useQuery} from "@apollo/client";
import classnames from "classnames";
import capitalize from "lodash/capitalize";
import {FaGaugeHigh} from "react-icons/fa6";

import Button from "@/components/button/Button.tsx";
import {TooltipWrapper} from "@/components/overlay/TooltipWrapper.tsx";
import {shortCurrency} from "@/lib/format.tsx";
import {GQL_PLAN_INFO, PlanInfoResult} from "@/lib/queries/billing.ts";
import {AddedValue, BillingPeriod, Plan} from "@/lib/types/billing.ts";
import {CheckFrequency} from "@/lib/types/devices.ts";
import {PlanRow} from "@/pages/billing/subscription/plan/PlanRow.tsx";
import {PlanRowAdditionalServices} from "@/pages/billing/subscription/plan/PlanRowAdditionalServices.tsx";
import {PlanRowCheckFrequency} from "@/pages/billing/subscription/plan/PlanRowCheckFrequency.tsx";
import {PlanRowCheckLocations} from "@/pages/billing/subscription/plan/PlanRowCheckLocations.tsx";
import {PlanRowStrategy} from "@/pages/billing/subscription/plan/PlanRowStrategy.tsx";
import {PlanRowUpro} from "@/pages/billing/subscription/plan/PlanRowUpro.tsx";

import css from "./PlanRegular.module.css";

export interface PlanRegularProps {
  plan: Plan;
  currentPlan: Plan;
  handleCheckout: (plan: Plan, addedValues: AddedValue[]) => void;
  name?: string;
  subtitle?: string;
  featured?: boolean;
  startHereText: string;
  btnText: string;
  disabled: boolean;
  billingPeriod: BillingPeriod;
}

const _buildTooltip = (tooltips: Record<Plan, string>, plan: Plan) => tooltips[plan];

export const PlanRegular = ({
  plan,
  currentPlan,
  handleCheckout,
  name,
  subtitle,
  featured = false,
  startHereText,
  btnText,
  disabled,
  billingPeriod,
}: PlanRegularProps) => {
  const {data} = useQuery<PlanInfoResult>(GQL_PLAN_INFO, {
    variables: {
      plan: plan,
      ignoreAccountPlan: true,
    },
  });
  const planOrder = [Plan.Starter, Plan.Essential, Plan.Premium, Plan.Custom];
  const isBelowCurrent = planOrder.indexOf(plan) <= planOrder.indexOf(currentPlan);
  const isOverLimit = data?.planInfo.isOverLimit && currentPlan != Plan.Custom;
  const price = billingPeriod === BillingPeriod.MONTH ? data?.planInfo.price_monthly : data?.planInfo.price_annual;
  const minAddedValues =
    (data?.planInfo?.limits &&
      Object.entries(data?.planInfo.limits)
        .map(([k, limit]) => ({
          limit_field: k,
          amount: Math.ceil(Math.max(0, limit.used - limit.included) / limit.bundleSize) * limit.bundleSize,
        }))
        .filter((v: AddedValue) => v.amount > 0)) ||
    [];
  return (
    <>
      <div
        className={classnames("col white-block rounded rounded-3", css.PlanContainer, {
          "mt-6": !featured && (!isOverLimit || isBelowCurrent),
          "mt-3": featured || isOverLimit,
          [css.FeaturedBorder]: featured,
          "white-block-border": !featured,
        })}
      >
        {featured && !isOverLimit && (
          <div
            className={classnames(
              css.CrownFeatured,
              "fw-bold fs-5 text-uppercase text-cap py-2 text-white text-center font-18"
            )}
          >
            Most Popular
          </div>
        )}
        {isOverLimit && !isBelowCurrent && (
          <div>
            <div
              className={classnames(
                css.CrownOverLimit,
                "rounded-top text-white crown-over-limit d-flex py-2 px-2 align-items-center gap-3"
              )}
            >
              <div>
                <FaGaugeHigh />
              </div>
              <div className="lh-sm">{`In order to subscribe to ${capitalize(name)} please adjust current check usage highlighted below.`}</div>
            </div>
          </div>
        )}
        <div className={classnames(`pt-3 pb-0 px-3 ${name}`, css.PlanHeading, {FeaturedBorder: featured})}>
          <h2 className={classnames("fw-bold mb-0", css.PlanTitle)}>{capitalize(name)}</h2>
          <div className={classnames(css.PlanSubtitle, "fw-bolder fs-7")}>
            <strong>{subtitle}</strong>
          </div>
          <div>
            <div className={classnames(`d-flex px-2 ${name}-price`, css.PlanPrice)}>
              <div className="d-flex flex-nowrap">
                <div className="fw-lighter text-gray-500">From</div>
                <div className={classnames("fw-bolder")}>&nbsp;{shortCurrency(price || 0)}/mo.</div>
              </div>
            </div>
          </div>
        </div>
        <div className={classnames(`px-3 ${name}`)}>
          <div className={classnames(`text-center p-3 ${name}`, {"px-1": featured, "px-2": !featured})}>
            <TooltipWrapper
              toolTip={
                isOverLimit && !isBelowCurrent
                  ? `In order to subscribe to ${capitalize(name)} please adjust current check usage highlighted below.`
                  : undefined
              }
            >
              <Button
                variant="marketingPrimary"
                className={`w-100`}
                onClick={() => handleCheckout(plan, minAddedValues)}
                disabled={disabled || (isOverLimit && !isBelowCurrent)}
              >
                {btnText ?? `Subscribe to ${capitalize(name)}`}
              </Button>
            </TooltipWrapper>
            <div className={classnames("mt-4 fw-bolder", css.PlanSubDescription)}>
              {startHereText ?? "Start here and grow:"}
            </div>
            <hr className="pulse-color-gray-500 border-2 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_basic_checks}
              name="Basic Check"
              tooltip="Continuously test website, network, and email server performance."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_transaction_checks}
              name="Transaction Check"
              tooltip="Mimic user actions to test forms and flows like login and checkout."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_api_checks}
              name="API Check"
              tooltip="Test APIs with multiple HTTP(S) requests, custom headers and tokens."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_pagespeed_checks}
              name="Page Speed Check"
              tooltip={_buildTooltip(
                {
                  [Plan.Starter]:
                    "Test website performance on scheduled intervals. Starter plan limits frequency and number " +
                    "of locations. Upgrade to Essential for additional PageSpeed tests.",
                  [Plan.Essential]: "Test website performance on scheduled intervals.",
                  [Plan.Premium]: "Test website performance on scheduled intervals.",
                  [Plan.Custom]: "Test website performance on scheduled intervals.",
                },
                plan
              )}
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_group_checks}
              name="Group Check"
              tooltip="Organize, monitor, alert, and report on the overall performance."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_statuspages}
              name="Status Page"
              tooltip="Customizable reporting for downtime, alerts, checks, and more."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_multi_users}
              unlimited={plan === Plan.Premium}
              name="User Account"
              tooltip="The total number of available platform login seats."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_sms}
              unlimited={plan === Plan.Premium}
              name="Monthly SMS Credit"
              tooltip="Get text message alert notifications."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_phonecalls}
              unlimited={plan === Plan.Premium}
              name="Monthly Phone Credit"
              tooltip="Get automated call alert notifications."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRowCheckLocations
              plan={plan}
              limits={data?.planInfo.limits.limit_monitoring_group_level}
              name="Check Locations"
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_monitoring_groups}
              name="Locations per Check"
              pluralizeName={false}
              tooltip="The number of probe servers locations configurable per check."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRowCheckFrequency
              included={data?.planInfo.limits.limit_check_frequency.included as CheckFrequency}
              name="Minimum Check Frequency"
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_cloudstatus_checks}
              name="Cloud Status Check"
              tooltip="Monitor the status of common cloud services."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_private_locations}
              name="Private Location"
              tooltip="Number of Private Locations available for monitoring."
            />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRow
              plan={plan}
              limits={data?.planInfo.limits.limit_rum_checks_requests_per_month}
              name="Real User Monitoring"
              tooltip="Monitor site load time, errors, and performance with real user sessions."
              pluralizeName={false}
              suffix="Page-View"
            />
            <PlanRowAdditionalServices />
            <PlanRowStrategy plan={plan} />
            <hr className="pulse-color-gray-500 my-2" />
            <PlanRowUpro plan={plan} />
          </div>
        </div>
      </div>
    </>
  );
};
